import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  UserPlus, Send, ExternalLink, Copy, Check, Loader2, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { SectionHeader } from './VisualAtoms';
import { TONE } from '../theme/adminVisualSystem';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Unified Artist Onboarding panel that lives between Status and Curator Rating
 * on the Open Call detail page. Always visible. Green tone.
 *
 * Three states:
 *   A) Not accepted, no artist profile  → "Accept + Convert to Artist" (does both in one click)
 *   B) Accepted, no artist profile      → "Convert to Artist"
 *   C) Has artist profile               → "Open Artist Profile" + "Send Artist Intake Form"
 */

// Green tone styling per spec
const PANEL_STYLE = {
  borderColor: 'rgba(126, 212, 160, 0.45)',
  background: 'rgba(126, 212, 160, 0.04)',
};

// Reusable green action button (success-tone, with subtle glow on hover/active)
function GreenButton({
  children,
  icon: Icon,
  onClick,
  disabled,
  loading,
  testId,
  variant = 'solid', // 'solid' (filled green) | 'outline'
  className = '',
}) {
  const base =
    'inline-flex items-center justify-center gap-2 px-3 py-2.5 text-[11px] font-mono font-medium uppercase tracking-[0.14em] rounded-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
  const solid =
    'bg-[#7ed4a0] text-black hover:bg-[#92dcae] hover:shadow-[0_0_18px_rgba(126,212,160,0.45)] active:scale-[0.98]';
  const outline =
    'bg-transparent text-[#7ed4a0] border border-[rgba(126,212,160,0.45)] hover:bg-[rgba(126,212,160,0.08)] hover:border-[#7ed4a0] hover:shadow-[0_0_14px_rgba(126,212,160,0.30)] active:scale-[0.98]';
  const cls = `${base} ${variant === 'solid' ? solid : outline} ${className}`;
  const ResolvedIcon = loading ? Loader2 : Icon;
  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      data-testid={testId}
      data-clickable="true"
      whileHover={!(disabled || loading) ? { y: -1.5 } : undefined}
      whileTap={!(disabled || loading) ? { scale: 0.97 } : undefined}
      className={cls}
      style={{ cursor: disabled || loading ? 'wait' : 'pointer' }}
    >
      {ResolvedIcon && <ResolvedIcon className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />}
      {children}
    </motion.button>
  );
}

export default function ArtistOnboardingBlock({ app, exhibitionId, onAppUpdate }) {
  const [busy, setBusy] = useState(false);
  const [intakeBusy, setIntakeBusy] = useState(false);
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);

  if (!app) return null;

  const isAccepted = (app.status || '') === 'accepted';
  const hasArtist = !!app.artist_profile_id;

  // ─── A: Convert flow (handles "accept + convert" or "convert" alone) ───
  const acceptIfNeeded = async () => {
    if (isAccepted) return app;
    const res = await axios.patch(`${API}/admin/open-call/${app.id}`, { status: 'accepted' });
    return res.data;
  };

  const convert = async () => {
    setBusy(true);
    try {
      // 1. Accept first if needed
      const updatedApp = await acceptIfNeeded();
      // 2. Convert
      const res = await axios.post(`${API}/admin/open-call/${app.id}/convert-to-artist`);
      const merged = {
        ...updatedApp,
        artist_profile_id: res.data.artist_profile_id,
        converted_to_artist_at: new Date().toISOString(),
      };
      onAppUpdate?.(merged);
      toast.success(isAccepted ? 'Artist profile created.' : 'Accepted and converted to artist.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to convert.');
    } finally {
      setBusy(false);
    }
  };

  // ─── B: Send intake link (sends email) ───
  const sendIntake = async () => {
    if (!exhibitionId) {
      toast.error('Select an exhibition first');
      return;
    }
    setIntakeBusy(true);
    try {
      const res = await axios.post(`${API}/admin/artists/${app.artist_profile_id}/intake-link`, {
        exhibition_id: exhibitionId,
        open_call_application_id: app.id,
        send_email: true,
        mode: 'assets_only',
        frontend_origin: window.location.origin,
      });
      setLink(res.data.link);
      if (res.data.email_sent) toast.success('Artist intake form sent.');
      else if (res.data.email_error) toast.error(`Email failed: ${res.data.email_error}`);
      else toast.success('Intake link generated.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send intake.');
    } finally {
      setIntakeBusy(false);
    }
  };

  // ─── B': Generate Short Form link silently and copy ───
  const copyShortFormLink = async () => {
    if (!exhibitionId) {
      toast.error('Select an exhibition first');
      return;
    }
    setIntakeBusy(true);
    try {
      const res = await axios.post(`${API}/admin/artists/${app.artist_profile_id}/intake-link`, {
        exhibition_id: exhibitionId,
        open_call_application_id: app.id,
        send_email: false,
        mode: 'assets_only',
        frontend_origin: window.location.origin,
      });
      setLink(res.data.link);
      try {
        await navigator.clipboard.writeText(res.data.link);
        toast.success('Short form link copied.');
      } catch {
        toast.message('Link generated. Copy manually below.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate link.');
    } finally {
      setIntakeBusy(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="border p-5"
      style={PANEL_STYLE}
      data-testid="artist-onboarding-block"
    >
      <SectionHeader
        icon={Sparkles}
        title="Artist Onboarding"
        helper={
          hasArtist
            ? 'Artist profile connected. Send the asset intake form to collect their materials.'
            : isAccepted
              ? 'Convert this applicant into a long-term artist profile and send the asset intake form.'
              : 'Applicant must be accepted before conversion.'
        }
      />

      {/* State A or B → Convert action */}
      {!hasArtist && (
        <div className="flex flex-wrap gap-2">
          <GreenButton
            icon={UserPlus}
            onClick={convert}
            disabled={busy}
            loading={busy}
            testId={isAccepted ? 'convert-to-artist-btn' : 'accept-and-convert-btn'}
            variant="solid"
          >
            {busy
              ? (isAccepted ? 'Converting…' : 'Accepting…')
              : (isAccepted ? 'Convert to Artist' : 'Accept + Convert to Artist')}
          </GreenButton>
        </div>
      )}

      {/* State C → Open Profile + Send Intake */}
      {hasArtist && (
        <>
          <div className="flex flex-wrap gap-2" data-testid="artist-onboarding-actions">
            <GreenButton
              icon={ExternalLink}
              onClick={() => window.open(`/admin/artists/${app.artist_profile_id}`, '_self')}
              testId="open-artist-profile-btn"
              variant="outline"
            >
              Open Artist Profile
            </GreenButton>
            <GreenButton
              icon={Send}
              onClick={sendIntake}
              disabled={intakeBusy}
              loading={intakeBusy}
              testId="send-artist-intake-btn"
              variant="solid"
            >
              {intakeBusy ? 'Sending…' : 'Send Artist Intake Form'}
            </GreenButton>
            <GreenButton
              icon={Copy}
              onClick={copyShortFormLink}
              disabled={intakeBusy}
              testId="copy-short-form-link-btn"
              variant="outline"
            >
              Copy Short Form Link
            </GreenButton>
          </div>

          {link && (
            <div
              className="mt-3 border bg-[#0a0a0a] p-2.5 flex items-center gap-2"
              style={{ borderColor: 'rgba(126,212,160,0.30)' }}
              data-testid="intake-link-display"
            >
              <p className="text-[10px] font-mono text-[#aaa] truncate flex-1" title={link}>
                {link}
              </p>
              <button
                type="button"
                onClick={copy}
                data-clickable="true"
                data-testid="copy-intake-link-btn"
                aria-label="Copy link"
                className="w-7 h-7 border flex items-center justify-center text-[#7ed4a0] hover:bg-[rgba(126,212,160,0.10)] transition-colors shrink-0"
                style={{ borderColor: 'rgba(126,212,160,0.40)' }}
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          )}

          {app.converted_to_artist_at && !link && (
            <p className="mt-3 text-[10px] font-mono" style={{ color: TONE.green.text, opacity: 0.7 }}>
              Converted · {new Date(app.converted_to_artist_at).toLocaleString()}
            </p>
          )}
        </>
      )}
    </motion.div>
  );
}
